// ORACLE (ASTRA) settings — idle spin on/off + seconds per full rotation.
// Columns: user_settings.oracle_spin_enabled (bool) / oracle_spin_seconds
// (number). CompassView reads the same columns.
import React from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { ThemedSwitch } from './ThemedSwitch';
import { makeThemedStyles } from '../../v10/theme';
import { SectionTitle, Chip, Field } from '../../v10/ui';
import { useSettingColumn } from './useSettingColumn';

const PRESETS = [7, 10, 15, 30, 60];

export default function OracleEditor({ userId }: { userId: string | undefined }) {
  const styles = useStyles();
  const { value: enabled, setValue: setEnabled } = useSettingColumn<boolean>(userId, 'oracle_spin_enabled', true);
  const { value: seconds, setValue: setSeconds } = useSettingColumn<number>(userId, 'oracle_spin_seconds', 15);

  const commitSeconds = (raw: string) => {
    const n = parseInt(raw, 10);
    if (Number.isFinite(n) && n > 0) setSeconds(n);
  };

  return (
    <View style={styles.content}>
      <SectionTitle style={styles.sectionGap}>ASTRA spin</SectionTitle>
      <Text style={styles.hint}>The ASTRA slowly rotates on its own when no orb is open.</Text>

      <View style={styles.row}>
        <Text style={styles.label}>Auto-spin</Text>
        <ThemedSwitch value={enabled} onValueChange={setEnabled} />
      </View>

      <View style={styles.row}>
        <Text style={styles.label}>Seconds per rotation</Text>
        <Field
          inputStyle={styles.numInput}
          keyboardType="number-pad"
          defaultValue={String(seconds)}
          onEndEditing={(e) => commitSeconds(e.nativeEvent.text)}
          editable={enabled}
        />
      </View>

      <View style={styles.presets}>
        {PRESETS.map((p) => (
          <Chip
            key={p}
            label={`${p}s`}
            active={seconds === p}
            onPress={enabled ? () => setSeconds(p) : undefined}
            style={!enabled ? styles.presetDisabled : undefined}
          />
        ))}
      </View>
    </View>
  );
}

const useStyles = makeThemedStyles(({ t, radii }) =>
  StyleSheet.create({
  sectionGap: { marginTop: 18 },
  content: { paddingHorizontal: 16, paddingBottom: 48, gap: 8 },
  hint: { fontSize: 12, color: t.ivorySoft, marginTop: 6, fontStyle: 'italic' },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: t.glass,
    borderRadius: radii.tile,
    paddingVertical: 12,
    paddingHorizontal: 14,
    marginTop: 8,
    borderWidth: 1,
    borderColor: t.lineFaint,
    boxShadow: `0 4px 14px ${t.glowSoft}`,
  },
  label: { fontSize: 14, fontWeight: '700', color: t.ivory },
  numInput: {
    minWidth: 64,
    textAlign: 'right',
    fontWeight: '700',
  },
  presets: { flexDirection: 'row', gap: 8, marginTop: 10, flexWrap: 'wrap' },
  presetDisabled: { opacity: 0.4 },
}),
);
